import * as Pioneer from "pioneer";
import { WMTSComponent } from "pioneer-scripts";
import { hideLoading, showLoading } from "../components/loading";
import { Config } from "../config/config";
import globalRefs, { setGlobalRef } from "./globalRefs";
import { isIphone, isIpad } from "../helpers/tools";
import {
  dateToUTCString,
  getDatasetFromVitalsData,
  getDefaultDataset,
  getDefaultDatasetByLayer,
  downloadXML,
} from "../helpers/tools";
import {
  datasetStore,
  layersStore,
  uiStore,
  spoutStore,
  lightStore,
} from "./globalState";

class TileManager {
  /**
   * Constructor
   * @param {object} pioneer Pioneer engine
   * @param {object} scene Pioneer main scene
   * @param {string} tile component id
   * @private
   */
  constructor(id) {
    !id && console.error("TileManager id is required");

    uiStore.setGlobalState({ loading: false });

    const { pioneer, datasetId } = globalRefs;
    this.pioneer = pioneer;
    this.scene = this.pioneer.getScene("main");

    this.id = id;

    setGlobalRef("datasetId", this.id);

    this.today = Date.now();

    this.dataset;
    this.isCubemap = false;
    this.animationMaxLevel = 4;
    this.maxLevel = Infinity;
    this.minLevel = 2;
    this.maxSet = "250m";
    this.pausedLevel = Infinity;

    this.ledger = new Set();

    // Array of functions with boolean returns. This can be extended in other classes
    this.fallbacks = [];

    this.colormapEntries = {};

    this.setEnabled = this.setEnabled.bind(this);
    this.hideWMTS = this.hideWMTS.bind(this);
    this.createWMTS = this.createWMTS.bind(this);
    this.preloadAnimation = this.preloadAnimation.bind(this);
    this.updateIndividualWMTS = this.updateIndividualWMTS.bind(this);

    this.unsubscribeBlueMarble = layersStore.subscribeTo(
      "blueMarble",
      (blueMarble) => {
        this.setBlueMarble(blueMarble);

        if (!blueMarble) {
          this.updateIndividualWMTS(
            datasetStore.stateSnapshot.currentIndex ?? 0,
          );
        }
      },
    );
  }

  /**
   * Set up callback functions from extended classes such as visible earth
   * @param {[function]} fallbacks array of callback functions
   */
  setFallbacks(fallbacks) {
    this.fallbacks = fallbacks;
  }

  /**
   * Update single wmts using the index and visibility
   * @param {number} index index of the wmts in the animation array
   * @param {boolean} visible visibility of the component
   */
  async updateIndividualWMTS(index, visible = true) {
    const { getManager } = globalRefs;
    const { animations } = getManager("dataset");
    const { rollingSubset } = this.dataset || {};

    let startDate;
    if (animations?.[index]) {
      const { dateObject } = animations[index];
      startDate = dateToUTCString(dateObject);
    } else {
      const { availableDates } = await this.getManifest();
      startDate = Object.keys(availableDates).pop();
    }

    const necessaryWMTS = new Set([startDate]);

    if (rollingSubset > 0 && rollingSubset > 0) {
      necessaryWMTS.clear();
      const { getManager } = globalRefs;
      const { animations } = getManager("dataset");

      for (let i = 0; i < animations.length; i += 1) {
        // gathering / setting up options
        const { layerId, type, dateObject } = animations[i];

        if (type === "wmts" && layerId) {
          for (let j = rollingSubset; j >= 0; j--) {
            const currentTime = new Date(dateObject);
            currentTime.setDate(currentTime.getDate() - j);
            necessaryWMTS.add(dateToUTCString(currentTime));
          }
        }
      }
    }

    for (const wmtsDate of necessaryWMTS) {
      const wmts = await this.createWMTS(wmtsDate, visible);

      if (!wmts) continue;

      this.earth = this.scene.getEntity("earth");

      // NOTE: make sure to revert code change in BH3 /home/pipeline/code/scripts/sync_earth_wmts.sh
      const { date, cubemapId } = wmts || {};
      this.isCubemap = false;
      if (cubemapId) {
        const spheroidLOD = this.earth.getComponent(
          Pioneer.SpheroidLODComponent,
        );

        this.isCubemap = true;
        spheroidLOD?.setEnabled(true);
        spheroidLOD?.setRenderingEnabled(true);

        const datasetManager = getManager("dataset");
        const [yyyy, mm, dd] = date.split("-");
        const yy = yyyy.slice(-2);

        if (yy?.length === 2 && mm?.length === 2 && dd?.length === 2) {
          const url = `${Config.dynamicAssetsUrl}/earth/data/${cubemapId}/${yy?.slice(-2)}/${mm}${dd}_`;
          try {
            await datasetManager.loadSpheroidTexture(url);
            await datasetManager.loadCanvas(url);
          } catch (e) {
            console.warn("e: ", e);
          }
        }

        this.hideWMTS();
        return;
      }
    }

    const keysToKeep = new Set(
      [...necessaryWMTS].map((wmtsDate) => this.GetKey(wmtsDate)),
    );
    this.hideWMTS(keysToKeep);
  }

  hideCubeMaps() {
    let i = 0;
    while (this.earth.getComponent(WMTSComponent, `cube${i}`) !== null) {
      this.earth
        .getComponent(WMTSComponent, `cube${i}`)
        .setRenderingEnabled(false);
      i++;
    }
  }

  /**
   * Hide wmts components except the one with 'key'
   * @param {Set} skip - skip key if available
   */
  hideWMTS(skip = new Set()) {
    this.earth = this.scene.getEntity("earth");

    for (const key of this.ledger) {
      const comp = this.earth.getComponent(WMTSComponent, key);
      // includes handle sub layer wmts databases such as NISAR
      if (skip.has(key)) {
        comp?.setRenderingEnabled(true);
        continue;
      }

      if (key === undefined || key === null) {
        if (comp) {
          this.earth.removeComponent(key);
        }
      } else {
        comp?.setRenderingEnabled(false);
      }
    }
  }

  /**
   * Get manifest content for this wmts
   * @returns object
   */
  async getManifest() {
    if (this.manifest) {
      return this.manifest;
    }

    const datasetPath = `${Config.dynamicAssetsUrl}/earth/data/${this.id}`;
    try {
      const response = await fetch(`${datasetPath}/dataset_manifest.json`, {
        cache: "no-store",
      });

      this.manifest = await response.json();
      this.manifest = Object.keys(this.manifest)
        .sort()
        .reduce((obj, key) => {
          obj[key] = this.manifest[key];
          return obj;
        }, {});

      return this.manifest;
    } catch (e) {
      console.warn("manifest fetch: ", e);
      return {};
    }
  }

  /**
   * Creates wmts key
   * @param {string} date - date of the component
   * @returns
   */
  GetKey(date) {
    return `${this.id}-${date}`;
  }

  /**
   * Creating json for color map entries
   * @param {string} layerId wmts layer id
   */
  async createColormapEntries(layerId) {
    const { colormap } = this.getLayerInfo(layerId);
    if (!colormap || this.colormapEntries[layerId]) {
      return;
    }

    try {
      const xml = await downloadXML(colormap);
      if (!xml) {
        return;
      }

      const colorMaps = xml.getElementsByTagName("ColorMaps")[0]?.children;
      for (let i = 0; i < colorMaps.length; i += 1) {
        for (let j = 0; j < colorMaps[i].children.length; j += 1) {
          const legend =
            colorMaps[i].getElementsByTagName("Legend")[0]?.children;
          for (let k = 0; k < legend.length; k += 1) {
            const tooltip = legend[k].getAttribute("tooltip");
            const rgb = legend[k].getAttribute("rgb");
            if (tooltip !== "No Data") {
              this.colormapEntries[layerId] =
                this.colormapEntries[layerId] || {};

              const [red, green, blue] = rgb.split(",");
              const values = tooltip.split(" ");
              this.colormapEntries[layerId][red] =
                this.colormapEntries[layerId][red] || {};
              this.colormapEntries[layerId][red][green] =
                this.colormapEntries[layerId][red][green] || {};
              for (let h = 0; h < values.length; h += 1) {
                if (!isNaN(values[h])) {
                  this.colormapEntries[layerId][red][green][blue] = parseFloat(
                    values[h],
                  );
                  break;
                }
              }
            }
          }
        }
      }
    } catch (e) {
      console.warn("Warning in createColormapEntries: ", layerId, e);
    }
  }

  /**
   * Calculate color data specific for this WMTS info
   * @returns {Number} - value
   */
  calculateDataFromColor(color) {
    if (arguments.length === 0) color = this.calculateColorFromRaycasting();
    if (!color) {
      return;
    }

    // if there is no color or color is pure black
    let red = parseInt(color.r);
    let green = parseInt(color.g);
    let blue = parseInt(color.b);

    if (
      !color ||
      red + green + blue === 0 ||
      !this.colormapEntries[this.layerId]
    ) {
      return null;
    }

    if (
      this.colormapEntries[this.layerId][red]?.[green]?.[blue] !== undefined
    ) {
      return this.colormapEntries[this.layerId][red][green][blue];
    }

    // if exact color match is not in color map entries, try 5 points up and down
    // also clamp between 0 and 255 values
    const wiggleRoom = 5;
    const direction = [-1, 1];

    for (let i = 0; i < direction.length; i += 1) {
      // find available red channel
      for (let j = 0; j < wiggleRoom; j += 1) {
        red = Math.min(255, Math.max(0, parseInt(color.r) + j * direction[i]));
        if (this.colormapEntries[this.layerId][red]) {
          break;
        }
      }

      // red channel is not found
      if (!this.colormapEntries[this.layerId][red]) {
        return null;
      }

      // find available green channel
      for (let j = 0; j < wiggleRoom; j += 1) {
        green = Math.min(
          255,
          Math.max(0, parseInt(color.g) + j * direction[i]),
        );
        if (this.colormapEntries[this.layerId][red][green]) {
          break;
        }
      }

      // green channel is not found
      if (!this.colormapEntries[this.layerId][red][green]) {
        return null;
      }

      // find available blue channel
      for (let j = 0; j < wiggleRoom; j += 1) {
        blue = Math.min(255, Math.max(0, parseInt(color.b) + j * direction[i]));
        if (
          this.colormapEntries[this.layerId][red][green][blue] !== undefined
        ) {
          return this.colormapEntries[this.layerId][red][green][blue];
        }
      }
    }

    return null;
  }

  /**
   * Uses the ray casting from the current mouse position to read the color
   * @returns {Array} - [red, green, blue]
   */
  calculateColorFromRaycasting(pixelSpacePosition) {
    const cameraEntity = this.scene.getEntity("camera");
    const earth = this.scene.getEntity("earth");
    const cameraComponent = cameraEntity.getComponent(Pioneer.CameraComponent);

    // Get the cursor position into camera-space (as a normalize direction), which is what getRaycast requires.
    const normalSpacePosition = new Pioneer.Vector3();
    const pickDirection = new Pioneer.Vector3();
    const pixelSpaceCursorPosition = pixelSpacePosition ?? cameraComponent
      .getViewport()
      .getEngine()
      .getInput()
      .getCursorPosition();
    cameraComponent
      .getViewport()
      .getNormalSpacePositionFromPixelSpacePosition(
        normalSpacePosition,
        pixelSpaceCursorPosition,
      );
    cameraComponent.getCameraSpacePositionFromNormalSpacePosition(
      pickDirection,
      normalSpacePosition,
    );
    pickDirection.normalize(pickDirection);

    const results = cameraComponent.getRaycastColor(
      Pioneer.Vector3.Zero,
      pickDirection,
      earth,
    );
    const color = results?.filter((result) => {
      const { object } = result || {};
      return object?.name?.includes("WMTSComponent");
    })?.[0]?.color;

    return color;
  }

  preloadAnimation() {
    const { getManager } = globalRefs;
    const { animations } = getManager("dataset");

    if (!animations || !Array.isArray(animations)) {
      return;
    }

    const { rollingSubset = 0 } = this.dataset || {};

    for (let i = 0; i < animations.length; i += 1) {
      // gathering / setting up options
      const { layerId, type, dateObject } = animations[i];

      if (type === "wmts" && layerId) {
        const currentTime = new Date(dateObject);

        if (rollingSubset > 0) {
          for (let j = rollingSubset; j >= 0; j--) {
            currentTime.setDate(currentTime.getDate() - j);

            // check for rolling datasets
            this.createWMTS(dateToUTCString(currentTime));
          }
        } else {
          this.createWMTS(dateToUTCString(currentTime));
        }
      }
    }
  }

  /**
   * Return layer info from vital_data.js
   * @param {string} layerId wmts layer id
   * @returns {obj}
   */
  getLayerInfo(layerId) {
    const { vitalData, dataset } = getDatasetFromVitalsData(this.id);

    for (let i = 0; i < vitalData?.datasetGroups?.length; i += 1) {
      const { datasets } = vitalData.datasetGroups[i];
      for (let j = 0; j < datasets.length; j += 1) {
        if (datasets[j].layerInfo?.[layerId]) {
          const layerInfo = datasets[j].layerInfo[layerId];

          layerInfo.maxSet = layerInfo.maxSet || this.maxSet;
          layerInfo.minLevel = layerInfo.minLevel || this.minLevel;
          layerInfo.maxLevel = layerInfo.maxLevel || this.maxLevel;

          // use max level of minLevel (2) for iphones and ipads
          if (dataset?.externalId === "radar" && (isIphone() || isIpad())) {
            layerInfo.maxLevel = layerInfo.minLevel;
          }

          return layerInfo;
        }
      }
    }

    return null;
  }

  /**
   * Enabling wmts component
   * @param {string} date - wmts date
   * @param {boolean} visible - wmts visibility
   * @returns
   */
  setEnabled(date = null, visible = true) {
    if (date) {
      const key = this.GetKey(date);

      const comp = this.earth?.getComponent(WMTSComponent, key);

      if (comp) {
        comp.setEnabled(visible);
        comp.setRenderingEnabled(visible);
        return;
      }
    }

    // enable last added WMTS component
    const nums = this.earth?.getNumComponents();
    for (let i = nums - 1; i >= 0; i--) {
      const elem = this.earth.getComponentAt(i);

      if (elem instanceof WMTSComponent) {
        elem.setEnabled(visible);
        elem.setRenderingEnabled(visible);
        return;
      }
    }
  }

  /**
   * Create wmts component using a key and date
   * @param {string} date this is the date of the wmts component
   * @returns {wmts}
   */
  async createWMTS(date = null, visible = false) {
    // handling leftover wmts initiations
    const { currentDataset } = datasetStore.stateSnapshot;
    const { externalId } = currentDataset || {};

    if (this.id !== externalId) return null;

    const { spout, spoutGlobe } = spoutStore.stateSnapshot;
    const { fill: lightFill } = lightStore.stateSnapshot;
    const { transparent } = this.dataset || {};

    this.earth = this.scene.getEntity("earth");

    this.manifest = await this.getManifest();

    const { availableDates } = this.manifest;
    const wmtsDate = date || Object.keys(availableDates).pop();

    if (!availableDates?.[wmtsDate]) {
      return null;
    }

    const { isDatasetPaused } = datasetStore.stateSnapshot;
    const { getManager } = globalRefs;

    const { isAnimating } = getManager("dataset");
    const { layerId, cubemapId } = availableDates[wmtsDate];

    if (cubemapId) {
      return { cubemapId, date };
    }

    const { endpoint, minLevel, maxLevel, maxSet } =
      this.getLayerInfo(layerId) || {};

    this.layerId = layerId;

    await this.scene.waitTillEntitiesInPlace(["earth"]);
    await this.pioneer.waitUntilNextFrame();

    const { datasetId } = globalRefs;

    if (datasetId !== this.id) return;

    const key = this.GetKey(date);
    if (this.earth.getComponent(WMTSComponent, key)) {
      this.earth.removeComponent(key);
    }

    const wmts = this.earth.addComponent(WMTSComponent, key);
    wmts.setLayer(layerId);
    wmts.setEndPoint(endpoint);
    wmts.setMinLevel(minLevel);
    wmts.setMaxLevel(
      isAnimating && !isDatasetPaused ? this.animationMaxLevel : maxLevel,
    );
    wmts.setTileMatrixSet(maxSet);
    wmts.setDimensionValue("Time", wmtsDate);
    wmts.setRenderingEnabled(visible);

    const { lightColorOn, lightColorOff } = globalRefs;
    wmts.setAmbientLightColor(lightFill ? lightColorOn : lightColorOff);

    if (transparent) {
      const patchSpheroid = this.earth.getComponent(
        Pioneer.SpheroidComponent,
        "patchSpheroid",
      );

      let baseFloor = this.earth.getComponent(
        Pioneer.SpheroidComponent,
        "baseFloor",
      );
      if (!baseFloor) {
        baseFloor = this.earth.addComponent(
          Pioneer.SpheroidComponent,
          "baseFloor",
        );

        baseFloor.setEquatorialRadius(patchSpheroid.getEquatorialRadius() - 50);
        baseFloor.setPolarRadius(patchSpheroid.getPolarRadius() - 50);
        baseFloor.setPlanetographic(patchSpheroid.isPlanetographic());
      }

      let baseLODFloor = this.earth.getComponent(
        Pioneer.SpheroidLODComponent,
        "baseLODFloor",
      );
      if (!baseLODFloor) {
        baseLODFloor = this.earth.addComponent(
          Pioneer.SpheroidLODComponent,
          "baseLODFloor",
        );
        baseLODFloor.setSpheroidReference("baseFloor");
        baseLODFloor.setMapping("cylinder");
        baseLODFloor.setTexture("color", "assets/images/black.webp", [8]);
      }

      baseLODFloor.setEnabled(true);
      baseLODFloor.setRenderingEnabled(true);

      // NOTE: this is added to update the order of components so the last added component is wmst.
      // Once the engine allows us to specify which component is our ground, we should remove this duplicate spheroid
      let wmtsSpheroid = this.earth.getComponent(
        Pioneer.SpheroidComponent,
        "wmtsSpheroid",
      );
      if (!wmtsSpheroid) {
        wmtsSpheroid = this.earth.addComponent(
          Pioneer.SpheroidComponent,
          "wmtsSpheroid",
        );
        wmtsSpheroid.setEquatorialRadius(patchSpheroid.getEquatorialRadius());
        wmtsSpheroid.setPolarRadius(patchSpheroid.getPolarRadius());
        wmtsSpheroid.setPlanetographic(patchSpheroid.isPlanetographic());
      }

      const wmtsDateObject = new Date(wmtsDate);
      const renderOrder = wmtsDateObject.getTime() / this.today;

      wmts.setTransparency(1.0);
      wmts.setTextureFilter("nearest");
      wmts.setRenderOrder(renderOrder);
      wmts.setSpheroidReference("wmtsSpheroid");
    }

    this.createColormapEntries(layerId);

    this.ledger.add(key);

    // Make globe emissive in Spout for bright lighting without setting ambient scene light
    if (spout === true && spoutGlobe === true) {
      wmts.setMaterialOption("colorMapEmissive", lightFill);
    }

    await wmts.getLoadedPromise(true);
    await this.pioneer.waitUntilNextFrame();

    datasetStore.setGlobalState({
      isTextureLoaded: true,
      wmtsLayerId: layerId,
    });

    return wmts;
  }

  /**
   * Check if snapshot is available. Otherwise try fallbacks if given and than blue marble
   * @param {object} wmts  current wmts component
   * @returns
   */
  async checkSnapshot(wmts) {
    const { cubemap } = this.dataset || {};

    const { template } = this.getLayerInfo(wmts._layerIdentifier);
    const time = wmts._dimensionValues.get("Time");
    if (!template || !time) {
      return;
    }

    uiStore.setGlobalState({ validData: true });
    const url = template.replace("{YYYY-MM-DD}", time);

    let invalid = false;
    try {
      const response = await fetch(url, { method: "HEAD" });
      invalid = response?.status !== 200;
    } catch (e) {
      invalid = true;
    }

    // if snapshot is accessible, stop here
    if (!invalid) return;

    // use cubmap if available
    if (cubemap) {
      const { getManager } = globalRefs;
      const datasetManager = getManager("dataset");
      const [yy, mm, dd] = time.split("-");
      const url = `${Config.dynamicAssetsUrl}/earth/data/${cubemap}/${yy?.slice(-2)}/${mm}${dd}_`;

      // check the first cubemap in case it's not valid
      const response = await fetch(`${url}0.webp`, { method: "HEAD" });

      if (response?.status === 200) {
        await datasetManager.loadSpheroidTexture(url);
        wmts.setRenderingEnabled(false);
        return;
      }
    }

    for (let i = 0; i < this.fallbacks.length; i += 1) {
      if (typeof this.fallbacks[i] === "function") {
        // fallback is successful
        const time = wmts._dimensionValues.get("Time");
        const success = await this.fallbacks[i](time);
        if (success) {
          wmts.setRenderingEnabled(false);
          return;
        }
      }
    }

    await this.scene.waitTillEntitiesInPlace(["earth"]);

    // if there is no fallback or none of them succeeded, go to blue marble
    wmts.setRenderingEnabled(false);
    uiStore.setGlobalState({ validData: false });
    this.setBlueMarble(true);
  }

  /**
   * Displays blue marble textures.
   * @param {boolean} showClouds
   */
  setBlueMarble(enabled, showClouds = false) {
    const { getManager, pioneer } = globalRefs;
    const { fill: lightFill } = lightStore.stateSnapshot;
    const spheroidLOD = this.scene
      .getEntity("earth")
      .getComponent(Pioneer.SpheroidLODComponent);

    if (!spheroidLOD.isEnabled() && enabled) {
      spheroidLOD.setEnabled(true);
    }

    if (!enabled) {
      const baseFloor = this.earth.getComponent(
        Pioneer.SpheroidComponent,
        "baseFloor",
      );
      baseFloor?.setRenderingEnabled(false);

      spheroidLOD.setRenderingEnabled(false);

      return;
    }

    // Enable spheroidLOD features.
    const { earthSpheroidFeatures } = getManager("content");

    const featuresToEnable = ["nightMap", "nightMapEmmissive"];
    showClouds && featuresToEnable.push("decalMap");

    // Enable spheroidLOD features.
    earthSpheroidFeatures.forEach((feature) => {
      if (!featuresToEnable.includes(feature)) {
        spheroidLOD.setMaterialOption(feature, false);
      }
    });
    featuresToEnable.forEach((feature) => {
      if (!earthSpheroidFeatures.includes(feature)) {
        spheroidLOD.setMaterialOption(feature, true);
      }
    });

    // DS: create a timeout of 0 which will trigger this right after everything is executed in this frame
    setTimeout(() => {
      // Set correct cube layout.
      spheroidLOD.setCubeMapLayout([
        ["+y", "+z", "+x"],
        ["-x", "+z", "+y"],
        ["-y", "+z", "-x"],
        ["+x", "+z", "-y"],
        ["+y", "-x", "+z"],
        ["+y", "+x", "-z"],
      ]);
    }, 0);

    // Set cube mapping.
    spheroidLOD.setMapping("cube");

    // Set textures.
    const minTextureSize = 512;
    const maxTextureSize = pioneer.getConfig().getValue("maxTextureSize");

    const firstTextureSize = [minTextureSize];
    const enableNightMap = !lightFill;
    spheroidLOD.setTexture(
      "color",
      "$STATIC_ASSETS_URL/maps/earth/color_$SIZE_$FACE.png",
      firstTextureSize,
    );
    if (enableNightMap) {
      spheroidLOD.setTexture(
        "night",
        "$STATIC_ASSETS_URL/maps/earth/night_$SIZE_$FACE.png",
        firstTextureSize,
      );
    }
    // Conditional cloud texture.
    if (showClouds) {
      spheroidLOD?.setTexture(
        "decal",
        "$STATIC_ASSETS_URL/maps/earth/cloud_$SIZE_$FACE.png",
        firstTextureSize,
      );
    }

    // Set feature to enable.
    showLoading("texture");

    this.scene.waitTillEntitiesInPlace(["earth"]).then(() => {
      // Hide cubemaps and WMTS.
      this.hideCubeMaps();
      this.hideWMTS();

      if (!spheroidLOD.isEnabled()) {
        spheroidLOD.setEnabled(true);
      }

      spheroidLOD.setRenderingEnabled(enabled);

      // Hide texture loading.
      hideLoading("texture");

      // If we're still on the blue marble and there is a larger maxTextureSize, we can load it.
      const { blueMarble } = layersStore.stateSnapshot;
      if (blueMarble && maxTextureSize > minTextureSize) {
        const secondTextureSize = [maxTextureSize];
        spheroidLOD.setTexture(
          "color",
          "$STATIC_ASSETS_URL/maps/earth/color_$SIZE_$FACE.png",
          secondTextureSize,
        );
        if (enableNightMap) {
          spheroidLOD.setTexture(
            "night",
            "$STATIC_ASSETS_URL/maps/earth/night_$SIZE_$FACE.png",
            secondTextureSize,
          );
        }
        // Conditional cloud texture.
        if (showClouds) {
          spheroidLOD?.setTexture(
            "decal",
            "$STATIC_ASSETS_URL/maps/earth/cloud_$SIZE_$FACE.png",
            secondTextureSize,
          );
        }
      }
    });
  }

  destroy() {
    for (const key of this.ledger) {
      if (this.earth.getComponent(WMTSComponent, key)) {
        this.earth.removeComponent(key);
      }
    }

    this.ledger.clear();

    this.unsubscribeBlueMarble();

    datasetStore.setGlobalState({ wmtsLayerId: null });
  }
}

export default TileManager;
